import React from 'react';
import { useParams } from 'react-router-dom';
import API_BASE_URL from '../config';
import { BRAND, brandFont, heroRule } from '../lib/brand';

// Public, brandable chat demo page at /chat-demo/:siteId — replaces the
// per-client static HTML copies (sdcera-demo.html, capaihss-demo.html).
// Rendered by the SPA so it works behind nginx's static-first routing with
// no server config; branding comes from the same origin-gated
// /api/widget-config the widget itself uses.
//
// One chat surface is mounted at a time: the on-page panel (easy to try
// immediately) or the floating bubble (the exact one-line embed a client
// site gets). Both are the same real embed script, not a simulation.

const FALLBACK_QUESTIONS = [
  'What does this organization do?',
  'When is the next public meeting?',
  'How do I contact staff?',
];

function agencyFromSiteId(siteId) {
  return siteId
    .replace(/-demo$/, '')
    .split('-')
    .map((w) => (w.length <= 4 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

const surfaceBtn = (active) => ({
  padding: '8px 16px',
  fontSize: '13px',
  fontWeight: 600,
  fontFamily: brandFont,
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  background: active ? BRAND.ink900 : 'transparent',
  color: active ? '#fff' : BRAND.gray600,
});

function ChatDemo() {
  const { siteId } = useParams();
  const [state, setState] = React.useState({ status: 'loading', widget: null });
  const [surface, setSurface] = React.useState('inline'); // 'inline' | 'float'
  const versionRef = React.useRef(Date.now()); // one bundle fetch per page load

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/widget-config`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ site_id: siteId }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setState({ status: 'notfound', widget: null });
          return;
        }
        setState({ status: 'ready', widget: data.widget || {} });
      } catch {
        if (!cancelled) setState({ status: 'notfound', widget: null });
      }
    })();
    return () => { cancelled = true; };
  }, [siteId]);

  const widget = state.widget || {};
  const agency = (widget.agency_name || '').trim() || agencyFromSiteId(siteId);
  const title = (widget.display_name || '').trim() || `${agency} Assistant`;
  const hasConfigStarters = (widget.starter_questions || []).length > 0;

  React.useEffect(() => {
    const prev = document.title;
    document.title = `${title} demo | Inquiros`;
    return () => { document.title = prev; };
  }, [title]);

  // Inject the real widget embed once branding is known (the widget reads its
  // own <script> tag's data-attrs via document.currentScript). Exactly one
  // surface mounts at a time; the toggle swaps the script tag.
  React.useEffect(() => {
    if (state.status !== 'ready') return undefined;
    const s = document.createElement('script');
    s.src = `/dist/clipt-widget.umd.js?v=${versionRef.current}`;
    s.dataset.mode = 'chat';
    s.dataset.siteId = siteId;
    s.dataset.backendUrl = API_BASE_URL;
    s.dataset.agencyName = agency;
    if (!hasConfigStarters) s.dataset.starterQuestions = FALLBACK_QUESTIONS.join('|');
    if (surface === 'inline') s.dataset.containerId = 'chat-root';
    else s.dataset.display = 'float';
    document.body.appendChild(s);
    return () => {
      s.remove();
      // The float embed mounts its own container on document.body; remove it
      // so toggling surfaces or leaving the page never strands a launcher.
      const floatRoot = document.getElementById('clipt-search-root');
      if (floatRoot) floatRoot.remove();
    };
  }, [state.status, siteId, agency, surface, hasConfigStarters]);

  const shell = (children) => (
    <div style={{ fontFamily: brandFont, background: BRAND.canvas, minHeight: '100vh', color: BRAND.ink900, display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: BRAND.surface, borderBottom: `1px solid ${BRAND.rule}` }}>
        <div style={{ maxWidth: '880px', margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <img src="/inquiros-logo.png" alt="Inquiros" style={{ height: '26px', display: 'block' }} />
          <span style={{ fontSize: '13px', color: BRAND.gray500 }}>Assistant demo</span>
        </div>
      </header>
      <main style={{ flex: 1, width: '100%', maxWidth: '880px', margin: '0 auto', padding: '36px 20px 48px', boxSizing: 'border-box' }}>
        {children}
      </main>
      <footer style={{ background: BRAND.surface, borderTop: `1px solid ${BRAND.rule}` }}>
        <div style={{ maxWidth: '880px', margin: '0 auto', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: BRAND.gray500 }}>
          <img src="/favicon.png" alt="" aria-hidden="true" style={{ width: '16px', height: '16px' }} />
          <span>Powered by Inquiros</span>
        </div>
      </footer>
    </div>
  );

  if (state.status === 'notfound') {
    return shell(
      <>
        <div style={heroRule} />
        <h1 style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 10px' }}>
          No demo here
        </h1>
        <p style={{ fontSize: '14px', color: BRAND.gray600, maxWidth: '560px', lineHeight: 1.6 }}>
          There's no active demo for{' '}
          <code style={{ background: '#EDF1F5', padding: '1px 6px', borderRadius: '4px' }}>{siteId}</code>{' '}
          at this address. If you just provisioned it, open the Ops Console and use the exact
          demo link it shows.
        </p>
      </>
    );
  }

  return shell(
    <>
      <div style={heroRule} />
      <h1 style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.02em', margin: '0 0 8px' }}>{title}</h1>
      <p style={{ fontSize: '14.5px', color: BRAND.gray500, lineHeight: 1.65, maxWidth: '640px', margin: '0 0 24px' }}>
        Answers come only from {agency}'s published materials, and every answer cites its
        sources. When something isn't published, the assistant says so instead of guessing.
      </p>

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '18px' }}>
        <div role="group" aria-label="Choose how the assistant is shown"
             style={{ display: 'inline-flex', gap: '4px', padding: '4px', background: BRAND.surface, border: `1px solid ${BRAND.rule}`, borderRadius: '8px' }}>
          <button type="button" style={surfaceBtn(surface === 'inline')}
                  aria-pressed={surface === 'inline'} onClick={() => setSurface('inline')}>
            On this page
          </button>
          <button type="button" style={surfaceBtn(surface === 'float')}
                  aria-pressed={surface === 'float'} onClick={() => setSurface('float')}>
            Floating bubble
          </button>
        </div>
        <span style={{ fontSize: '13px', color: BRAND.gray500 }}>
          {surface === 'inline'
            ? 'Also available as a floating bubble, the way it ships on a client site.'
            : 'The bubble in the corner is the one-line embed a client site gets.'}
        </span>
      </div>

      {/* Keys force a fresh DOM node per branch: the widget mounts its own React
          root inside #chat-root, so a reused node would keep that DOM alive
          across a surface switch. */}
      {state.status === 'loading' ? (
        <p key="loading" style={{ color: BRAND.gray400, fontSize: '14px' }}>Loading the demo…</p>
      ) : surface === 'inline' ? (
        <div key="inline" id="chat-root" style={{ marginBottom: '18px' }} />
      ) : (
        <div key="float" style={{ background: BRAND.surface, border: `1px solid ${BRAND.rule}`, borderRadius: '10px', padding: '18px 20px', marginBottom: '18px', maxWidth: '640px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: BRAND.gray600, lineHeight: 1.65 }}>
            Open the chat bubble in the corner to start the conversation. Branding, starter
            questions, and disclaimers are all set from the admin, with no code changes on
            the site.
          </p>
        </div>
      )}

      <p style={{ fontSize: '13px', color: BRAND.gray500, lineHeight: 1.6, maxWidth: '640px' }}>
        Tip: ask something unrelated, like "How do I renew my driver's license?", to watch
        it decline rather than guess.
      </p>
    </>
  );
}

export default ChatDemo;
